import { useEffect } from 'react';
import type { GateNext } from './LessonGate';

/**
 * Every lesson page ships with an in-page "Up next" card that already
 * has the right title and a friendly description for the next lesson,
 * plus a static "Coming soon" button. This component finds that button
 * at mount and swaps its behavior in place:
 *
 *   - If the lesson is not yet complete: button reads "Mark complete"
 *     and, on click, calls the server complete endpoint and navigates
 *     to the next lesson if unlocked.
 *   - If the lesson is complete and next is unlocked: button reads
 *     "Next lesson →" and navigates. A "Completed on <date>" line is
 *     inserted above the button.
 *   - If the lesson is complete and next is locked (drip): button is
 *     disabled with "Unlocks <date>" text.
 *   - If there is no next lesson (8.5): button reads "Back to dashboard"
 *     and links to /dashboard.
 *
 * No visible UI is rendered by this component itself: it only mutates
 * the existing NextUp card. This keeps every lesson page's DOM as its
 * own source of truth for the description text.
 */
export default function NextUpEnhancer({
  next,
  onComplete,
  completedAt,
}: {
  next: GateNext | null;
  onComplete: () => Promise<void>;
  completedAt: string | null;
}) {
  useEffect(() => {
    // Find the NextUp card. Every page uses this shape:
    //   <div class="mt-12 rounded-lg border border-line bg-surface p-6">
    //     <p ...>Up next · ...</p>
    //     <div ...>
    //       <div><h3>Title</h3><p>Desc</p></div>
    //       <button ...>Coming soon</button>
    //     </div>
    //   </div>
    const card = findNextUpCard();
    if (!card) return;

    const btn = card.querySelector<HTMLButtonElement>('button');
    if (!btn) return;

    // Save original state so we can restore on unmount.
    const originalText = btn.textContent;
    const originalClassName = btn.className;
    const originalDisabled = btn.disabled;
    const originalOnClick = btn.onclick;

    // The button's parent (the right-hand side of the flex row) may need
    // an unlock-hint line beneath the button.
    const rightSide = btn.parentElement;

    // Clear any prior locked-hint under the button.
    const existingHint = card.querySelector<HTMLSpanElement>(
      '[data-nextup-hint]'
    );
    if (existingHint) existingHint.remove();

    // Case: final lesson (no next).
    if (!next) {
      btn.textContent = 'Back to dashboard →';
      btn.className =
        'shrink-0 rounded-md bg-amber px-4 py-2 text-sm font-semibold text-ink hover:bg-amber-hover';
      btn.disabled = false;
      btn.onclick = (e) => {
        e.preventDefault();
        window.location.href = '/dashboard';
      };
      if (completedAt) upsertStatus(card, statusText(completedAt));
      return cleanup;
    }

    // Case: lesson already complete.
    if (completedAt) {
      upsertStatus(card, statusText(completedAt));

      if (next.unlocked) {
        btn.textContent = `Next: Lesson ${next.slug} →`;
        btn.className =
          'shrink-0 rounded-md bg-amber px-4 py-2 text-sm font-semibold text-ink hover:bg-amber-hover';
        btn.disabled = false;
        btn.onclick = (e) => {
          e.preventDefault();
          window.location.href = `/preview/lesson/${next.slug}`;
        };
      } else {
        // Locked next lesson.
        const dateStr = next.unlockAt
          ? new Date(next.unlockAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            })
          : 'soon';
        btn.textContent = `Lesson ${next.slug} locked`;
        btn.className =
          'shrink-0 cursor-not-allowed rounded-md border border-line/60 bg-surface/40 px-4 py-2 text-sm font-semibold text-muted';
        btn.disabled = true;
        btn.onclick = null;
        addHintUnderButton(
          rightSide,
          next.crossesWeek
            ? `Week ${next.weekNumber} unlocks ${dateStr}`
            : `Unlocks ${dateStr}`
        );
      }
      return cleanup;
    }

    // Case: not yet complete. Show Mark complete button.
    btn.textContent = 'Mark complete';
    btn.className =
      'shrink-0 rounded-md bg-amber px-4 py-2 text-sm font-semibold text-ink hover:bg-amber-hover';
    btn.disabled = false;
    btn.onclick = async (e) => {
      e.preventDefault();
      btn.disabled = true;
      btn.textContent = 'Saving…';
      try {
        await onComplete();
        // onComplete updates React state; when this component re-renders
        // it re-runs the effect and the button transitions to the
        // "already complete" branch above.
      } catch (err) {
        console.error('[NextUpEnhancer] mark-complete failed', err);
        btn.disabled = false;
        btn.textContent = 'Mark complete';
        alert('Could not mark this lesson complete. Try again in a moment.');
      }
    };

    function cleanup() {
      btn!.textContent = originalText;
      btn!.className = originalClassName;
      btn!.disabled = originalDisabled;
      btn!.onclick = originalOnClick;
      const st = card!.querySelector('[data-nextup-status]');
      if (st) st.remove();
      const hint = card!.querySelector('[data-nextup-hint]');
      if (hint) hint.remove();
    }

    return cleanup;
  }, [next?.slug, next?.unlocked, next?.unlockAt, next?.crossesWeek, completedAt]);

  return null;
}

function statusText(completedAt: string) {
  const dt = new Date(completedAt);
  const formatted = dt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `✓ Completed on ${formatted}`;
}

function upsertStatus(card: HTMLElement, text: string) {
  let statusLine = card.querySelector<HTMLParagraphElement>(
    '[data-nextup-status]'
  );
  const header = card.querySelector<HTMLParagraphElement>(
    'p.font-mono'
  );
  if (!statusLine) {
    statusLine = document.createElement('p');
    statusLine.setAttribute('data-nextup-status', '');
    statusLine.className = 'mt-2 text-xs text-amber';
    // Insert directly after the "Up next · Lesson X.Y" header line.
    if (header && header.parentElement === card) {
      header.after(statusLine);
    } else {
      card.prepend(statusLine);
    }
  }
  statusLine.textContent = text;
}

function addHintUnderButton(rightSide: HTMLElement | null, text: string) {
  if (!rightSide) return;
  const hint = document.createElement('span');
  hint.setAttribute('data-nextup-hint', '');
  hint.className = 'mt-2 block text-right text-xs text-muted';
  hint.textContent = text;
  rightSide.appendChild(hint);
}

/**
 * Find the in-page "Up next" card.
 *
 * Preferred: data-nextup-card attribute on the card wrapper (every lesson
 * page's NextUp component has this). Fallback: content-based match on any
 * bordered surface card containing a font-mono "Up next" header. If a page
 * ever omits the card (like 8.5), this returns null and the enhancer no-ops.
 */
function findNextUpCard(): HTMLElement | null {
  const tagged = document.querySelector<HTMLElement>('[data-nextup-card]');
  if (tagged) return tagged;

  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(
      'div.border.border-line.bg-surface'
    )
  );
  for (let i = candidates.length - 1; i >= 0; i--) {
    const el = candidates[i];
    const header = el.querySelector('p.font-mono');
    if (header && /up next/i.test(header.textContent || '')) {
      return el;
    }
  }
  return null;
}
